import * as React from "react";
import * as _ from "lodash";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import { Storage } from "aws-amplify";

import {
  FilterParams,
  PaginatedParams,
  PaginatedData
} from "src/library/utils";
import { Text } from "src/library/components/Text/Text";
import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import Table, {
  ITableInstance,
  BulkSelectState
} from "src/library/components/Tables/Table";
import { TabbedNavigation } from "src/library/components/TabbedNavigation";
import { Select } from "src/library/components/Forms";
import { IAction } from "src/library/components/Button/ActionButton";
import type { IStore } from "src/core/shared/store";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import { orderImportErrorsTableColumns } from "src/core/shared/components/tables/process-job-errors/orderImportErrorsTable";
import {
  ListOrdersByProcessJobParams,
  ordersApi,
  IOrder
} from "src/core/orders/api/orders.api";
import {
  useProcessJobsApi,
  ImportFileParams,
  BulkFixErrorParams,
  IProcessJob,
  IProcessJobError
} from "src/core/process-job/api/processJobs.api";
import { PageProps } from "src/pages/Router/routes";
import { DropZone } from "src/core/shared/components/dropzones/DropZone";
import {
  Layout,
  MainContentWrapper,
  InfoBarWrapper
} from "src/core/shared/components/Layout";
import { ICustomer } from "src/core/customers/api/customers.api";
import { adminOrderTableColumns } from "src/core/shared/components/tables";
import { ProcessJobSidebar } from "src/core/process-job/components/ProcessJobSideBar";
import {
  IOrderImportSetting,
  OrderImportRequestName,
  useOrderImportApi
} from "src/modules/order-import/api/order-import.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import ConfirmIgnoreModal from "src/core/orders/components/Modals/confirmIgnoreModal";

interface OrderImportPageRouteProps {
  importId: string;
}

const defaultPagination: PaginatedParams<ICustomer> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "id", "ASC" ] ]
};

const OrderImportPage: React.ReactFCWithChildren<PageProps<unknown, OrderImportPageRouteProps>> = props => {
  const {
    orderStatuses, role, erp, installedModules
  } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const [ createImportJobResponse, createImportJobRequest ] = useProcessJobsApi("IMPORT_FILE");
  const [ getProcessJobResponse, getProcessJobRequest ] = useProcessJobsApi("GET_PROCESS_JOB");
  const [ ignoreErrorResponse, ignoreErrorRequest ] = useProcessJobsApi("BULK_IGNORE_PROCESS_JOB_ERROR");
  const [ retryErrorResponse, retryErrorRequest ] = useProcessJobsApi("BULK_RETRY_PROCESS_JOB_ERROR");

  const [ getProcessJobErrorsResponse, getProcessJobErrorsRequest ] = useProcessJobsApi(
    "GET_PROCESS_JOB_ERRORS"
  );

  const [ getProcessJobOrdersResponse, getProcessJobOrdersRequest ] = ordersApi<PaginatedData<IOrder>>("LIST_PROCESS_JOB_ORDERS");
  const [ importOrderSettingsResponse, importOrderSettingsRequest ] = useOrderImportApi(OrderImportRequestName.LIST_ORDER_IMPORT_SETTINGS);
  const [ exportOrderImportErrorsResponse, getExportImportErrorsRequest ] = useOrderImportApi(OrderImportRequestName.EXPORT_ORDER_IMPORT_ERRORS);
  const [ importTemplate, setImportTemplate ] = React.useState<null | string>(null);
  const TableRef = React.useRef<ITableInstance<IProcessJobError<"import-file-order">>>(null);
  const OrderTableRef = React.useRef<ITableInstance<IOrder>>(null);
  const [ processJob, setProcessJob ] = React.useState<IProcessJob | null>(null);
  const isLoading = ignoreErrorResponse.pending || getProcessJobResponse.pending || getProcessJobErrorsResponse.pending || getProcessJobOrdersResponse.pending;
  const importInterval = React.useRef<NodeJS.Timeout | boolean>(false);
  const [ importType, setImportType ] = React.useState<ImportFileParams["type"]>("orders");
  const [ openModal, setOpenModal ] = React.useState<BulkFixErrorParams | null>(null);
  const [ selectedImportOption, setSelectedImportOption ] = React.useState<IOrderImportSetting | null>(null);

  const importId = React.useMemo(() => {
    return props.location.pathname.split("/").slice(-1)[ 0 ];
  }, [ props.location ]);

  // import the file
  const processFile = React.useCallback((key: string) => {
    createImportJobRequest({
      s3Key: key,
      type: importType,
      orderImportSettingId: selectedImportOption?.id
    });
  }, [
    createImportJobRequest,
    importType,
    selectedImportOption
  ]);

  const reloadJobData = React.useCallback(
    (refreshErrors = false) => {
      if (importId !== "new") {
        getProcessJobRequest(parseInt(importId, 10));
      }

      if (refreshErrors) {
        if (TableRef.current) {
          TableRef.current.resetPagination();
          TableRef.current.reloadTable();
        }
      }
    },
    [ importId ]
  );

  const reloadOrderData = React.useCallback(() => {
    if (OrderTableRef.current) {
      OrderTableRef.current.reloadTable();
    }
  }, [ OrderTableRef ]);

  // reload when
  React.useEffect(() => {
    if (importId !== "new") {
      reloadJobData();
    } else {
      setProcessJob(null);
    }
  }, [ importId ]);

  // create a new import job
  React.useEffect(() => {
    if (createImportJobResponse.data) {
      props.history.push(`/admin/orders/imports/${createImportJobResponse.data.id}`);
      setProcessJob(createImportJobResponse.data);
    }
  }, [ createImportJobResponse ]);

  // get the process job
  React.useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (getProcessJobResponse.data) {
      setProcessJob(getProcessJobResponse.data);

      if (getProcessJobResponse.data.finishedAt && TableRef.current) {
        TableRef.current.reloadTable();
      }

      if (getProcessJobResponse.data.status.match(/queued|in_progress/)) {
        if (!interval) {
          interval = setInterval(() => {
            reloadJobData();
          }, 10000);
        }
      }
    }

    if (interval) {
      clearInterval(interval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [ getProcessJobResponse, reloadJobData ]);

  // get the process job
  React.useEffect(() => {
    if (processJob) {
      if (processJob.status.match(/queued|in_progress/) !== null) {
        if (!importInterval.current) {
          importInterval.current = setInterval(() => {
            reloadJobData(true);
          }, 10000);
        }
      } else if (typeof importInterval.current === "number") {
        clearInterval(importInterval.current);
      }

      return () => {
        if (typeof importInterval.current === "number") {
          clearInterval(importInterval.current);
        }
      };
    }

    return () => null;
  }, [ processJob ]);

  // refetches errors
  const onFetchErrorData = React.useCallback(
    (filters: FilterParams<IProcessJobError>, pagination: PaginatedParams<IProcessJobError>) => {
      if (importId) {
        getProcessJobErrorsRequest({
          processJobId: parseInt(importId, 10),
          filters,
          pagination
        });
      }
    },
    [ importId ]
  );

  // refetches errors
  const onFetchOrdersData = React.useCallback(
    (filters: FilterParams<IOrder>, pagination: PaginatedParams<IOrder>) => {
      if (importId) {
        getProcessJobOrdersRequest<ListOrdersByProcessJobParams>({
          processJobId: parseInt(importId, 10),
          filters,
          pagination
        });
      }
    },
    [ importId ]
  );

  React.useEffect(() => {
    if (ignoreErrorResponse.data) {
      setProcessJob(ignoreErrorResponse.data);

      if (TableRef.current) {
        TableRef.current.resetPagination();
        TableRef.current.reloadTable();
      }
    } else if (ignoreErrorResponse.error) {
      toast.error(`Failed to resolve errors - ${ignoreErrorResponse.error.message}`);

      if (TableRef.current) {
        TableRef.current.resetPagination();
        TableRef.current.reloadTable();
      }
    }
  }, [ ignoreErrorResponse ]);

  React.useEffect(() => {
    if (retryErrorResponse.data) {
      setProcessJob(retryErrorResponse.data);

      if (TableRef.current) {
        TableRef.current.resetPagination();
        TableRef.current.reloadTable();
      }
    } else if (retryErrorResponse.error) {
      toast.error(`Failed to retry errors - ${retryErrorResponse.error.message}`);

      if (TableRef.current) {
        TableRef.current.resetPagination();
        TableRef.current.reloadTable();
      }
    }
  }, [ retryErrorResponse ]);

  /**
   * Bulk actions
   */
  const bulkActions = React.useCallback(
    (bulkState: BulkSelectState) => {
      if (selectedImportOption?.type && [ "order-predictions", "order-picks" ].includes(selectedImportOption?.type)) {
        return [] as IAction[];
      }

      const actions = [
        {
          title: "Mark as resolved",
          action: () => setOpenModal({
            errorIds: _.values(bulkState),
            processJobId: parseInt(importId, 10)
          })
        }
      ];

      if (selectedImportOption?.type === "orders") {
        actions.push({
          title: "Retry",
          action: () => retryErrorRequest({
            errorIds: _.values(bulkState),
            processJobId: parseInt(importId, 10)
          })
        });
      }

      return actions as IAction[];
    },
    [ importId, selectedImportOption?.type ]
  );

  const importOptions = React.useMemo(() => {
    // map options from order import settings options
    if (importOrderSettingsResponse.data) {
      if (processJob?.jobData) {
        const selectedOption = _.find(importOrderSettingsResponse.data, importSetting => importSetting.id === processJob.jobData.orderImportSettingId);

        if (selectedOption) {
          setSelectedImportOption(selectedOption);
        }
      }

      const availableOptions = importOrderSettingsResponse.data.filter(setting => !!setting.options.allowManual);

      return availableOptions.map(setting => ({
        text: _.startCase(_.toLower((setting.name).replace(/[^a-zA-Z\s]/g, " "))),
        value: setting.name
      }));
    }

    return [];
  }, [ importOrderSettingsResponse.data, processJob?.jobData ]);

  // get order import settings to generate options on mount
  React.useEffect(() => {
    importOrderSettingsRequest(null);
  }, [ importOrderSettingsRequest ]);

  React.useEffect(() => {
    Storage.get("assets/templates/orders.csv", { level: "public" }).then(url => {
      setImportTemplate(url as string);
    });
  }, []);

  // handle export process job response
  React.useEffect(() => {
    if (exportOrderImportErrorsResponse.data) {
      toast.success(exportOrderImportErrorsResponse.data.message);
    }

    if (exportOrderImportErrorsResponse.error) {
      toast.error(exportOrderImportErrorsResponse.error.message);
    }
  }, [ exportOrderImportErrorsResponse ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        {importOrderSettingsResponse.pending && <LoadingSpinner type="circular" />}

        {importId === "new" && !processJob && importOrderSettingsResponse.data && (
          <Grid.Bounds width="100%">
            <ImportFormWrapper width="threeQuarters">
              <Text as="p">
                Start a new order import by dragging a new file into the area below. Once the import starts it can take a few minutes to run. You do
                not need to stay on this page, we will let you know when the import is complete!
              </Text>

              <Grid.Box>
                <Select
                  className="import-type-select"
                  name="order-import-type"
                  label="Order import type"
                  as="select"
                  unselectedOption={{
                    text: "Please select",
                    value: ""
                  }}
                  handleChange={e => {
                    const chosenImport = _.find(importOrderSettingsResponse.data, setting => setting.name === e.target.value);

                    if (chosenImport) {
                      setSelectedImportOption(chosenImport);
                    }

                    setImportType(e.target.value);
                  }}
                  options={importOptions}
                />

                {selectedImportOption && (
                  <DropZone
                    fileType={selectedImportOption.fileType}
                    error={createImportJobResponse.error ? createImportJobResponse.error.message : null}
                    loading={createImportJobResponse.pending}
                    processFile={processFile}
                    s3Path={selectedImportOption ? (selectedImportOption.s3Path).replace(/public\//g, "") : "imports/orders"}
                    dropzoneMessage="Drag and Drop a file to start a new import orders"
                  />
                )}
              </Grid.Box>
            </ImportFormWrapper>

            <TemplatesWrapper width="quarter">
              {importTemplate && (
                <Button onClick={() => window.open(importTemplate, "_blank")}>
                  Download template
                </Button>
              )}
            </TemplatesWrapper>
          </Grid.Bounds>
        )}

        {!!processJob && (
          <>
            {processJob.status === "queued" && (
              <Text as="p">
                Your import has been queued. This page will automatically update to show progress.
              </Text>
            )}

            <ConfirmIgnoreModal
              show={openModal ? true : false}
              handleDismiss={() => setOpenModal(null)}
              onIgnoreErrors={() => {
                openModal ? ignoreErrorRequest(openModal) : null;
              }}
            />

            <TabbedNavigation
              tabs={[
                {
                  title: "Unresolved Errors",
                  content: () => (
                    <>
                      <Text as="p">
                        Showing unresolved errors. To complete the import please mark errors as resolved or retry importing the errored lines.
                      </Text>

                      <Table
                        bulkActionOptions={bulkActions}
                        onFetchData={onFetchErrorData}
                        ref={TableRef}
                        tableId={`order-import-${processJob.id}`}
                        columns={orderImportErrorsTableColumns({ terminology: props.terminology })}
                        tableOf="Order Import errors"
                        data={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.result : []}
                        error={getProcessJobErrorsResponse.error}
                        totalRows={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.count : 0}
                        pages={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.pages : 0}
                        loading={isLoading}
                        defaultPagination={defaultPagination}
                        defaultFilters={{ status: "error" }}
                        topLevelButtons={[
                          {
                            title: "Export Errors",
                            disabled: !getProcessJobErrorsResponse.data?.count,
                            onClick: () => {
                              getExportImportErrorsRequest({ processJobId: processJob.id });
                            },
                            loading: exportOrderImportErrorsResponse.pending
                          }
                        ]}
                      />
                    </>
                  )
                },
                {
                  title: "Resolved Errors",
                  content: () => (
                    <>
                      <Text as="p">
                        Showing previously resolved errors that require no furter action!
                      </Text>

                      <Table
                        onFetchData={onFetchErrorData}
                        ref={TableRef}
                        tableId={`order-import-${processJob.id}`}
                        columns={orderImportErrorsTableColumns({ terminology: props.terminology })}
                        tableOf="Resolved Errors"
                        data={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.result : []}
                        error={getProcessJobErrorsResponse.error}
                        totalRows={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.count : 0}
                        pages={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.pages : 0}
                        loading={isLoading}
                        defaultPagination={defaultPagination}
                        defaultFilters={{ status: [ "ignored", "resolved" ] }}
                      />
                    </>
                  )
                },
                {
                  title: "Warnings",
                  content: () => (
                    <>
                      <Text as="p">
                        Products were successfully imported but the order contained warnings.
                      </Text>

                      <Table
                        onFetchData={onFetchErrorData}
                        ref={TableRef}
                        tableId={`order-import-${processJob.id}`}
                        columns={orderImportErrorsTableColumns({ terminology: props.terminology })}
                        tableOf="Resolved Errors"
                        data={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.result : []}
                        error={getProcessJobErrorsResponse.error}
                        totalRows={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.count : 0}
                        pages={getProcessJobErrorsResponse.data ? getProcessJobErrorsResponse.data.pages : 0}
                        loading={isLoading}
                        defaultPagination={defaultPagination}
                        defaultFilters={{ status: "warn" }}
                      />
                    </>
                  )
                },
                {
                  title: "Orders",
                  content: () => (
                    <>
                      <Text as="p">
                        Showing all orders affected by this import
                      </Text>

                      <Table
                        tableId={`order-import-orders-${processJob.id}`}
                        tableOf={props.terminology.order.plural.text}
                        totalRows={getProcessJobOrdersResponse.data ? getProcessJobOrdersResponse.data.count : 0}
                        pages={getProcessJobOrdersResponse.data ? getProcessJobOrdersResponse.data.pages : 0}
                        loading={isLoading}
                        data={getProcessJobOrdersResponse.data ? getProcessJobOrdersResponse.data.result : []}
                        ref={OrderTableRef}
                        columns={adminOrderTableColumns({
                          installedModules,
                          terminology: props.terminology,
                          orderStatuses,
                          ActionButton: row => {
                            if (erp && role) {
                              return (
                                <OrderActionsList
                                  erp={erp}
                                  terminology={props.terminology}
                                  role={role}
                                  order={row.original}
                                  reload={reloadOrderData}
                                />
                              );
                            }

                            return null;
                          }
                        })}
                        onFetchData={onFetchOrdersData}
                        defaultPagination={defaultPagination}
                        error={getProcessJobOrdersResponse.error}
                        mainAction={row => props.history.push(`/orders/${row.original.id}`)}
                      />
                    </>
                  )
                }
              ]}
            />
          </>
        )}
      </MainContentWrapper>

      <InfoBarWrapper>
        {!!processJob && (
          <ProcessJobSidebar
            isLoading={false}
            data={processJob}
          />
        )}
      </InfoBarWrapper>
    </Layout>
  );
};

export default OrderImportPage;

const TemplatesWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    padding: 0 ${theme.spacing.padding.lg};
  `}
`;

const ImportFormWrapper = styled(Grid.Box)`
  .import-type-select {
    margin-bottom: 2rem;
  }
`;
